/**
 * AI INTAKE PAGE — "The Interview Chair"
 * Innovation Hub | Miskulin Technologies
 *
 * This is where an inventor sits down with the AI and describes their idea.
 * Two modes:
 *   1. VOICE: Click record, speak naturally. Whisper transcribes in real-time.
 *   2. CHAT:  Type messages. AI responds with follow-up questions.
 *
 * The AI acts as a patent attorney — probing for the "defining factor"
 * that makes this invention novel. It asks 5-12 follow-up questions,
 * then generates a structured technical summary.
 *
 * Everything is encrypted (Black Box). No human sees the raw conversation
 * until the creator signs an NDA.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabase';

const INTAKE_STAGES = [
  { key: 'problem', label: 'The Problem', desc: 'What problem does your invention solve?' },
  { key: 'solution', label: 'Your Solution', desc: 'How does your invention solve it?' },
  { key: 'mechanism', label: 'How It Works', desc: 'Walk me through the technical details.' },
  { key: 'differentiator', label: 'What\'s Different', desc: 'What makes this different from existing solutions?' },
  { key: 'applications', label: 'Applications', desc: 'Where would this be used? What industries?' },
  { key: 'summary', label: 'Summary', desc: 'Let me confirm I understand your invention.' },
];

const AI_GREETING = `Welcome to the Innovation Hub AI Intake Engine.

I'm here to help you document your invention idea with the precision of a patent filing. Everything you share is encrypted and protected — no human will see your raw responses until you sign an NDA.

Let's start at the beginning: **What problem does your invention solve?** Describe the frustration, the gap in the market, or the inefficiency you've identified.`;

export default function AIIntakePage() {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [sessionStatus, setSessionStatus] = useState('idle'); // idle, active, processing, complete
  const [submissionResult, setSubmissionResult] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const timerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Start a new intake session
  const startSession = async () => {
    setSessionStatus('active');
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    setMessages([{
      role: 'ai',
      content: AI_GREETING,
      timestamp: new Date().toISOString(),
    }]);
    setCurrentStage(0);
    setQuestionCount(1);

    // Create session in Supabase
    try {
      await supabase.from('intake_sessions').insert({
        id: newSessionId,
        creator_id: user?.id,
        status: 'ACTIVE',
        messages: [{ role: 'ai', content: AI_GREETING }],
      });
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  };

  // Send a text message to the AI
  const sendMessage = async () => {
    if (!inputText.trim() || isProcessing) return;

    const userMessage = {
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    try {
      // Call the intake API
      const response = await fetch('/api/intake/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage.content,
          stage: INTAKE_STAGES[currentStage]?.key,
          question_count: questionCount,
        }),
      });

      if (!response.ok) {
        // Fallback: generate a local follow-up if API isn't deployed yet
        const fallbackResponse = generateLocalFollowUp(userMessage.content, currentStage, questionCount);
        setMessages(prev => [...prev, {
          role: 'ai',
          content: fallbackResponse.content,
          timestamp: new Date().toISOString(),
        }]);

        if (fallbackResponse.advanceStage) {
          setCurrentStage(prev => Math.min(prev + 1, INTAKE_STAGES.length - 1));
        }
        setQuestionCount(prev => prev + 1);

        if (fallbackResponse.complete) {
          setSessionStatus('processing');
          setTimeout(() => processSubmission(), 2000);
        }
      } else {
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: 'ai',
          content: data.ai_response,
          timestamp: new Date().toISOString(),
        }]);

        if (data.advance_stage) {
          setCurrentStage(prev => Math.min(prev + 1, INTAKE_STAGES.length - 1));
        }
        setQuestionCount(prev => prev + 1);

        if (data.complete) {
          setSessionStatus('processing');
          processSubmission();
        }
      }
    } catch (err) {
      // Offline/API not deployed — use local fallback
      const fallbackResponse = generateLocalFollowUp(inputText.trim(), currentStage, questionCount);
      setMessages(prev => [...prev, {
        role: 'ai',
        content: fallbackResponse.content,
        timestamp: new Date().toISOString(),
      }]);
      if (fallbackResponse.advanceStage) {
        setCurrentStage(prev => Math.min(prev + 1, INTAKE_STAGES.length - 1));
      }
      setQuestionCount(prev => prev + 1);
      if (fallbackResponse.complete) {
        setSessionStatus('processing');
        setTimeout(() => processSubmission(), 2000);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Local fallback follow-up generator (used when API isn't deployed yet)
  const generateLocalFollowUp = (userInput, stage, qCount) => {
    const stageQuestions = {
      0: [ // Problem
        "That's a real pain point. Can you give me a specific example of when this problem caused the most frustration?",
        "Who experiences this problem most — individuals, businesses, or a specific industry?",
      ],
      1: [ // Solution
        "Interesting approach. Walk me through the step-by-step: if I were using your invention, what happens first?",
        "Does your solution require any special materials, technology, or infrastructure to work?",
      ],
      2: [ // Mechanism
        "Now let's get technical. How does the core mechanism actually function? What are the moving parts?",
        "If this were a machine, what would the key components be? How do they interact?",
        "Is there a specific physical or chemical process involved?",
      ],
      3: [ // Differentiator
        "What's the closest existing solution you're aware of? How is yours fundamentally different?",
        "If a competitor tried to replicate this, what would be the hardest part for them to figure out?",
        "Is there a specific 'aha moment' or 'trick' that makes your approach work where others don't?",
      ],
      4: [ // Applications
        "Which industry would benefit most from this right now?",
        "Could this be adapted for other fields? What modifications would be needed?",
      ],
      5: [ // Summary
        "Thank you. I've captured the essence of your invention. Let me process this and generate your technical summary, prior art search, and patent readiness score.",
      ],
    };

    const questions = stageQuestions[stage] || stageQuestions[5];
    const questionIndex = qCount % questions.length;

    const shouldAdvance = qCount > 0 && (qCount % 2 === 0) && stage < 5;
    const isComplete = stage >= 5 || qCount >= 12;

    return {
      content: isComplete
        ? "Thank you for walking me through your invention in such detail. I now have enough information to generate your:\n\n1. **Technical Summary** (encrypted)\n2. **Prior Art Search** (across 5 patent databases)\n3. **Uniqueness Score** (0-100)\n4. **Viability Assessment**\n5. **Patent Draft** (USPTO provisional format)\n\nProcessing now... This typically takes 2-3 minutes."
        : questions[questionIndex],
      advanceStage: shouldAdvance,
      complete: isComplete,
    };
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Audio level visualization
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const updateLevel = () => {
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(avg / 128);
        animationRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      // Timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        cancelAnimationFrame(animationRef.current);
        clearInterval(timerRef.current);
        setAudioLevel(0);
        stream.getTracks().forEach(t => t.stop());

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start(250); // Collect chunks every 250ms
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
      alert('Microphone access is required for voice intake. Please allow microphone access and try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Transcribe audio via Whisper API
  const transcribeAudio = async (audioBlob) => {
    setIsProcessing(true);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('session_id', sessionId);

      const response = await fetch('/api/intake/audio', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setInputText(data.transcript);
      } else {
        // Fallback: show message that voice transcription requires API deployment
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'Voice transcription requires the AI backend to be deployed. For now, please type your response.',
          timestamp: new Date().toISOString(),
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Voice recording captured. Transcription service is offline — please type your response instead.',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  // Process the completed intake session
  const processSubmission = async () => {
    try {
      const response = await fetch('/api/intake/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissionResult(data);
      } else {
        // Simulate result for demo/offline
        setSubmissionResult({
          submission_id: crypto.randomUUID(),
          status: 'PROCESSING',
          message: 'Your idea has been encrypted and queued for processing. You will receive results within 2-3 minutes.',
          estimated_time: '2-3 minutes',
        });
      }
    } catch (err) {
      setSubmissionResult({
        submission_id: crypto.randomUUID(),
        status: 'QUEUED',
        message: 'Submission recorded. AI processing will begin when the backend is deployed.',
      });
    }
    setSessionStatus('complete');
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ===== RENDER =====

  // Idle state — "Start Intake" screen
  if (sessionStatus === 'idle') {
    return (
      <div className="page-body" style={{ padding: 24 }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h1 style={{ color: 'var(--text)', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
            AI Intake Engine
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 32 }}>
            Describe your invention. Our AI will interview you like a patent attorney.
          </p>

          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16,
            padding: 40, textAlign: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F9E0;</div>
            <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 600, marginBottom: 12 }}>
              Ready to Document Your Invention?
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 8, maxWidth: 480, margin: '0 auto 24px' }}>
              Our AI will guide you through a structured interview to extract the key details
              of your invention. You can speak or type — everything is encrypted.
            </p>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 32,
              maxWidth: 500, margin: '0 auto 32px'
            }}>
              {[
                { icon: '&#x1F512;', label: 'End-to-End Encrypted', desc: 'Split-key AES-256-GCM' },
                { icon: '&#x1F50D;', label: '5 Patent Databases', desc: 'USPTO, EPO, WIPO, Google, Scholar' },
                { icon: '&#x1F4C4;', label: 'Patent Draft', desc: 'USPTO provisional format' },
              ].map((f, i) => (
                <div key={i} style={{ padding: 12 }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: f.icon }} />
                  <div style={{ color: 'var(--text)', fontSize: 12, fontWeight: 600 }}>{f.label}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>{f.desc}</div>
                </div>
              ))}
            </div>

            <button
              onClick={startSession}
              style={{
                padding: '14px 40px', background: '#6366f1', color: '#fff', border: 'none',
                borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => e.target.style.background = '#4f46e5'}
              onMouseLeave={e => e.target.style.background = '#6366f1'}
            >
              Start AI Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Complete state — show results
  if (sessionStatus === 'complete' && submissionResult) {
    return (
      <div className="page-body" style={{ padding: 24 }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h1 style={{ color: 'var(--text)', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
            Submission Complete
          </h1>

          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16,
            padding: 32, textAlign: 'center', marginBottom: 24
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#x2705;</div>
            <h2 style={{ color: 'var(--text)', fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
              Your Idea Has Been Secured
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
              {submissionResult.message}
            </p>

            <div style={{
              background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
              padding: 20, textAlign: 'left', marginBottom: 24
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Submission ID</div>
                  <div style={{ color: 'var(--text)', fontSize: 13, fontFamily: 'monospace', marginTop: 4 }}>
                    {submissionResult.submission_id?.slice(0, 8)}...
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</div>
                  <div style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12,
                    fontWeight: 600, marginTop: 4,
                    background: submissionResult.status === 'PROCESSING' ? '#fef3c7' : '#dbeafe',
                    color: submissionResult.status === 'PROCESSING' ? '#92400e' : '#1e40af',
                  }}>
                    {submissionResult.status}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Questions Asked</div>
                  <div style={{ color: 'var(--text)', fontSize: 13, marginTop: 4 }}>{questionCount}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Encryption</div>
                  <div style={{ color: '#10b981', fontSize: 13, fontWeight: 600, marginTop: 4 }}>AES-256-GCM Active</div>
                </div>
              </div>
            </div>

            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
              What happens next:
            </p>
            <div style={{ textAlign: 'left', maxWidth: 400, margin: '0 auto' }}>
              {[
                'Prior art search across 5 patent databases',
                'Uniqueness score calculated (0-100)',
                'Viability assessment generated',
                'Patent draft created (USPTO provisional format)',
                'Blockchain timestamp recorded on Base L2',
                'Certificate of proof-of-origin issued',
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', background: 'var(--bg)',
                    border: '2px solid var(--border)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 10, color: 'var(--text-muted)', flexShrink: 0
                  }}>{i + 1}</span>
                  <span style={{ color: 'var(--text)', fontSize: 13 }}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => { setSessionStatus('idle'); setMessages([]); setSubmissionResult(null); setQuestionCount(0); setCurrentStage(0); }}
              style={{
                flex: 1, padding: '12px 24px', background: 'var(--card)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, cursor: 'pointer'
              }}
            >
              Submit Another Idea
            </button>
            <button
              onClick={() => window.location.href = '/submissions'}
              style={{
                flex: 1, padding: '12px 24px', background: '#6366f1', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              View My Submissions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active session — chat interface
  return (
    <div className="page-body" style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>
      {/* Stage progress bar */}
      <div style={{
        padding: '12px 24px', background: 'var(--card)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 8
      }}>
        {INTAKE_STAGES.map((stage, i) => (
          <React.Fragment key={stage.key}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                background: i <= currentStage ? '#6366f1' : 'var(--bg)',
                color: i <= currentStage ? '#fff' : 'var(--text-muted)',
                border: i <= currentStage ? 'none' : '1px solid var(--border)',
                transition: 'all 0.3s',
              }}>{i + 1}</div>
              <span style={{
                fontSize: 11, color: i === currentStage ? 'var(--text)' : 'var(--text-muted)',
                fontWeight: i === currentStage ? 600 : 400,
                display: window.innerWidth > 768 ? 'inline' : 'none'
              }}>{stage.label}</span>
            </div>
            {i < INTAKE_STAGES.length - 1 && (
              <div style={{
                flex: 1, height: 2, maxWidth: 40,
                background: i < currentStage ? '#6366f1' : 'var(--border)',
                transition: 'background 0.3s',
              }} />
            )}
          </React.Fragment>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
          Q: {questionCount}/12
        </div>
      </div>

      {/* Messages area */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '24px 24px 100px',
        maxWidth: 720, margin: '0 auto', width: '100%'
      }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex', flexDirection: 'column',
            alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 16,
          }}>
            <div style={{
              maxWidth: '85%', padding: '12px 16px', borderRadius: 12,
              background: msg.role === 'user' ? '#6366f1' : msg.role === 'system' ? '#fef3c7' : 'var(--card)',
              color: msg.role === 'user' ? '#fff' : msg.role === 'system' ? '#92400e' : 'var(--text)',
              border: msg.role === 'ai' ? '1px solid var(--border)' : 'none',
              fontSize: 14, lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.role === 'ai' && (
                <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, marginBottom: 4 }}>
                  AI Patent Analyst
                </div>
              )}
              {msg.content.split('**').map((part, j) =>
                j % 2 === 1 ? <strong key={j}>{part}</strong> : <span key={j}>{part}</span>
              )}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, padding: '0 4px' }}>
              {new Date(msg.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))}

        {isProcessing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
            <div style={{
              display: 'flex', gap: 4, alignItems: 'center'
            }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 8, height: 8, borderRadius: '50%', background: '#6366f1',
                  animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>AI is thinking...</span>
          </div>
        )}

        {sessionStatus === 'processing' && (
          <div style={{
            padding: 20, background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 12, textAlign: 'center'
          }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>&#x2699;&#xFE0F;</div>
            <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>Processing Your Submission</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              Encrypting data, searching prior art, calculating scores...
            </div>
            <div style={{
              width: '100%', height: 4, background: 'var(--border)', borderRadius: 2,
              marginTop: 12, overflow: 'hidden'
            }}>
              <div style={{
                width: '60%', height: '100%', background: '#6366f1', borderRadius: 2,
                animation: 'progress 2s ease-in-out infinite',
              }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      {sessionStatus === 'active' && (
        <div style={{
          position: 'sticky', bottom: 0, padding: '16px 24px', background: 'var(--card)',
          borderTop: '1px solid var(--border)',
        }}>
          <div style={{
            maxWidth: 720, margin: '0 auto', display: 'flex', gap: 8, alignItems: 'flex-end'
          }}>
            {/* Voice record button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              style={{
                width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                background: isRecording ? '#ef4444' : 'var(--bg)',
                color: isRecording ? '#fff' : 'var(--text)',
                border: isRecording ? 'none' : '1px solid var(--border)',
                transition: 'all 0.2s',
                boxShadow: isRecording ? `0 0 0 ${audioLevel * 20}px rgba(239,68,68,0.1)` : 'none',
              }}
              title={isRecording ? 'Stop recording' : 'Start voice recording'}
            >
              {isRecording ? (
                <span style={{ fontSize: 10, fontWeight: 700 }}>{formatTime(recordingTime)}</span>
              ) : (
                <span style={{ fontSize: 18 }}>&#x1F3A4;</span>
              )}
            </button>

            {/* Text input */}
            <div style={{ flex: 1, position: 'relative' }}>
              <textarea
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Describe your invention... (Shift+Enter for new line)"
                disabled={isProcessing || isRecording}
                rows={1}
                style={{
                  width: '100%', padding: '10px 14px', background: 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text)',
                  fontSize: 14, resize: 'none', minHeight: 44, maxHeight: 120,
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
            </div>

            {/* Send button */}
            <button
              onClick={sendMessage}
              disabled={!inputText.trim() || isProcessing}
              style={{
                width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                background: inputText.trim() ? '#6366f1' : 'var(--bg)',
                color: inputText.trim() ? '#fff' : 'var(--text-muted)',
                border: inputText.trim() ? 'none' : '1px solid var(--border)',
                transition: 'all 0.2s',
                opacity: isProcessing ? 0.5 : 1,
              }}
            >
              <span style={{ fontSize: 18 }}>&#x27A4;</span>
            </button>
          </div>

          <div style={{ maxWidth: 720, margin: '8px auto 0', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              &#x1F512; End-to-end encrypted &middot; AES-256-GCM
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Stage: {INTAKE_STAGES[currentStage]?.label}
            </span>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
}
